import { MetaRatingDelta } from "@/components/MetaRatingDelta";
import FC24Player from "@/components/molecules/Player/FC24/FC24Player";
import { cn } from "@/lib/utils";
import { EvolutionBuilderPlayer } from "@/redux/evolutions/evolutionsBuilderSlice";
import { PlayStylesLabRecommendation } from "@/redux/playStylesLab/playStylesLabSlice";
import { playerRoleLabelSelector } from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";
import { memo } from "react";
import { useSelector } from "react-redux";

import { PlayStyleIcon } from "./PlayStyleCard";

type Props = {
  recommendation: PlayStylesLabRecommendation;
  player: EvolutionBuilderPlayer | null;
  onSelect?: (recommendation: PlayStylesLabRecommendation) => void;
};

const IconRow = ({
  label,
  ids,
  plus,
}: {
  label: string;
  ids: number[];
  plus: boolean;
}) => {
  const { t } = useTranslation("players");
  return (
    <div className="flex items-center w-full">
      <span className="w-[92px] shrink-0 text-sm text-dark-tertiary leading-6">
        {label}
      </span>
      <div className="flex flex-1 min-w-0 items-center gap-x-2 rounded bg-inner-box px-4 py-3">
        {ids.length > 0 ? (
          ids.map((id) => (
            <PlayStyleIcon key={id} id={id} plus={plus} className="size-8" />
          ))
        ) : (
          <span className="text-sm text-dark-tertiary leading-6 h-8 flex items-center justify-center">
            {t("playStylesLab.recommendations.noSlotsLeft")}
          </span>
        )}
      </div>
    </div>
  );
};

export const PlayStyleRecommendationCard = memo(
  function PlayStyleRecommendationCard({
    recommendation,
    player,
    onSelect,
  }: Props) {
    const roleLabel = useSelector(
      playerRoleLabelSelector({
        playerRoleId: recommendation.playerRoleId,
        appendPosition: true,
      }),
    );
    const { t } = useTranslation(["players", "common"]);

    return (
      <button
        onClick={() => onSelect?.(recommendation)}
        className={cn(
          "w-full rounded-lg bg-panel-middleground py-2.5 px-3 text-left",
          "transition-colors duration-200 hover:bg-new-gray-700",
        )}
      >
        {/* Mobile: packed icon grid + stacked meta / optimized-for */}
        <div className="flex w-full items-stretch gap-x-2 desktop:hidden">
          <div className="flex w-[137px] shrink-0 flex-col justify-center gap-y-4 rounded bg-inner-box p-3">
            <div className="flex flex-wrap gap-1.5">
              {recommendation.playStylesPlus.map((id) => (
                <PlayStyleIcon key={`plus-${id}`} id={id} plus />
              ))}
              {recommendation.playStyles.map((id) => (
                <PlayStyleIcon key={id} id={id} plus={false} />
              ))}
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-y-2 mb-auto">
            <div className="flex w-full items-center desktop:justify-end rounded bg-inner-box p-2 text-center">
              <MetaRatingDelta
                metaRating={recommendation.metaRating.value}
                delta={recommendation.metaRating.delta}
              />
            </div>
            <div className="flex w-full flex-col items-center gap-y-1 rounded bg-inner-box p-2 text-center">
              <span className="text-sm font-medium text-dark-tertiary leading-5">
                {t("playStylesLab.recommendations.optimisedFor")}
              </span>
              <p className="text-sm font-semibold relative shrink-0 text-dark-secondary">
                {roleLabel}
              </p>
            </div>
          </div>
        </div>

        {/* Desktop: player + labeled rows + meta / optimized-for column */}
        <div className="hidden w-full items-center gap-x-2.5 desktop:flex">
          {player && (
            <div className="flex h-[170px] shrink-0 -mt-1 scale-90 items-center justify-center">
              <FC24Player
                isMobile={false}
                largeMobile
                hidePrice
                chemStyleId={0}
                {...player}
              />
            </div>
          )}

          <div className="ml-1 flex min-w-0 flex-1 flex-col justify-center gap-y-5">
            <IconRow
              label={t("playStylesLab.playStylesPlus")}
              ids={recommendation.playStylesPlus}
              plus
            />
            <IconRow
              label={t("playStylesLab.playStyles")}
              ids={recommendation.playStyles}
              plus={false}
            />
          </div>

          <div className="flex w-[180px] h-[132px] shrink-0 flex-col items-start justify-center gap-y-4 rounded-lg bg-inner-box p-4">
            <div className="flex flex-col items-start gap-y-1">
              <span className="text-sm font-medium text-dark-tertiary leading-5">
                {t("playStylesLab.recommendations.optimisedFor")}
              </span>
              <p className="text-sm font-semibold relative shrink-0 text-dark-secondary text-nowrap">
                {roleLabel}
              </p>
            </div>
            <div className="flex w-full flex-col items-start gap-y-1">
              <span className="text-sm font-medium text-dark-tertiary leading-5">
                {t("common:metaRating")}
              </span>
              <MetaRatingDelta
                className="justify-end"
                metaRating={recommendation.metaRating.value}
                delta={recommendation.metaRating.delta}
              />
            </div>
          </div>
        </div>
      </button>
    );
  },
);
